import React, { useState } from 'react'
import moment from 'moment'
import { DateTime } from 'luxon'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IDistributorResponseData, IVoltLicenseData } from '@cbgms/api/support'
import { AppBar, Box, IconButton, LinearProgress, Toolbar, Typography } from '@mui/material'
import IconSearch from '@mui/icons-material/Search'
import { useQuery } from '@cbgms/base/http/use-query'
import { useNotifier } from '@cbgms/base/notifier'
import { getDistributors, getHaynesParams, getHaynesVOLTLicenses, postNewHaynesLicense } from 'app-support/licenses/state/actions'
import HaynesProMoreActionsMenu from './HaynesProMoreActionsMenu'
import HaynesProMessage, { IMessageState } from './HaynesProMessage'
import { apiFormat } from '@cbgms/base/utils/datetime'
import AppBarDropDown from './AppBarDropDown'
import AppBarDatePicker from './AppBarDatePicker'
import HaynesProLicensesTable from './HaynesProLicensesTable'
import { NullableDate } from './date.d'

const style = {
  appBar: {
    // needs negative margins to remove parent's padding, since we're mixing semantic and material
    marginLeft: -1,
    marginRight: -1,
    marginTop: -1,
    zIndex: 10, // can't be more then 11, language selector will overlap
    width: 'initial'
  },
  appBarActions: {
    display: 'inline-flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  distributorSelector: {
    minWidth: '14rem'
  },
  optionSelector: {
    width: '7rem'
  },
  table: {
    marginTop: 1
  },
  title: {
    whiteSpace: 'nowrap'
  }
}

const currentDate = moment()
const nextMonth = moment().add(1, 'month')

const Wrap: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  return <Box sx={{ display: 'inline-block' }}>{children}</Box>
}

const HaynesProLicensesOverview: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const [startDate, setStartDate] = useState<NullableDate>(currentDate)
  const [endDate, setEndDate] = useState<NullableDate>(nextMonth)
  const [distributor, setDistributor] = useState<IDistributorResponseData | null>(null)
  const [reseller, setReseller] = useState<string | null>(null)
  const [productType, setProductType] = useState<string | null>(null)
  const [message, setMessage] = useState<IMessageState | null>(null)
  const [rows, setRows] = useState<IVoltLicenseData[] | null>(null)
  const [rowSelections, setRowSelections] = useState<Map<string, boolean>>(new Map())
  const [isRenewingLicenses, setIsRenewingLicenses] = useState<boolean>(false)
  const [renewingProgress, setRenewingProgress] = useState<number>(0)

  // Load distributors
  const distributorResult = useQuery(['HaynesProLicensesOverview', 'getDistributors'], async () => {
    const { Data } = await dispatch(getDistributors({ fields: '*' }))
    return Data
  })

  // load the resellers and product types for the distributors
  const distributorParamsResult = useQuery(
    ['HaynesProLicensesOverview', 'getHaynesParams', distributor?.Distributor.HaynesPro?.DistributorID],
    async () => {
      const empty = {
        productTypeOptions: [],
        ResellerOptions: []
      }
      if (!distributor?.Distributor.HaynesPro?.DistributorID) {
        return empty
      }
      try {
        const { Data } = await dispatch(getHaynesParams(distributor?.Distributor.HaynesPro?.DistributorID))
        return {
          productTypeOptions: [{ text: t('global.all'), value: '' }, ...Data.ProductType.map(t => ({ text: t.Name, value: `${t.ID}` }))],
          ResellerOptions: [{ text: t('global.all'), value: '' }, ...Data.Resellers.map(r => ({ text: r.Name, value: `${r.ID}` }))]
        }
      } catch {
        return empty
      }
    }
  )

  if (distributorResult.isError) {
    notifier.error(t('global.something_went_wrong'))
  }
  const { data } = distributorResult
  const disabledToolbar = !distributor?.Distributor?.UUID

  const handleChange = (value: NullableDate, setDate: (date: NullableDate) => void) => {
    value && value.isValid() && setDate(value)
  }
  const searchLicenses = async () => {
    try {
      const { Data } = await dispatch(
        getHaynesVOLTLicenses(distributor?.Distributor.HaynesPro?.DistributorID || '', {
          ResellerID: reseller!,
          ProductTypeID: productType!,
          ExpiresBeforeDate: endDate?.format(apiFormat.yearMonthDay) || undefined,
          LicenseSearchFlag: 'ActiveLicense',
          IncludeCustomerData: true,
          IncludeShopData: true
        })
      )
      setRows(Data || null)

      setRowSelections(new Map(Data.map(row => [row.LicenseID, false])))
    } catch {
      setRows(null)
      notifier.error(t('global.something_went_wrong'))
    }
  }

  const handleMessage = (m: IMessageState) => {
    setMessage(m)
  }

  const renewSelectedLicenses = async () => {
    if (isRenewingLicenses) {
      return
    }

    setRenewingProgress(0)
    setIsRenewingLicenses(true)

    try {
      await Promise.all(
        Array.from(rowSelections)
          .filter(([_, selected]) => selected)
          .map(async ([licenseID]) => {
            const existingLicense = rows?.filter(row => row.LicenseID === licenseID)[0]

            const newLicense = {
              DistributorID: Number(distributor!.Distributor.HaynesPro!.DistributorID!),
              CustomerID: existingLicense!.CustomerID!,
              StartDate: DateTime.fromISO(existingLicense!.EndDate).plus({ days: 1 }).toISO(),
              EndDate: DateTime.fromISO(existingLicense!.EndDate).plus({ years: 1 }).toISO(),
              ProductTypeID: Number(existingLicense!.ProductType.ID)
            }

            await dispatch(postNewHaynesLicense(newLicense))

            setRenewingProgress(previous => {
              return previous + 1
            })
          })
      )
    } catch {
      // error is already notified elsewhere
    } finally {
      setIsRenewingLicenses(false)
    }

    // refresh search results so renewed licenses will disappear from the list
    searchLicenses()
  }

  return (
    <>
      <AppBar position='relative' sx={style.appBar}>
        <Toolbar>
          <Typography variant='h6' sx={style.title}>
            {t('haynespro.page_title_licenses')}
          </Typography>
          <Box sx={style.appBarActions}>
            <Wrap>
              <AppBarDropDown
                label={t('haynespro.select_distributor')}
                value={distributor?.Distributor.UUID || ''}
                onChange={eventData => {
                  const newDistributor = data?.find(i => i.Distributor.UUID === eventData.target.value) || null
                  setDistributor(newDistributor)
                }}
                sx={style.distributorSelector}
                options={data?.map(item => ({ text: item.Distributor.Name, value: item.Distributor.UUID }))}
                data-testid='distributor-dropdown'
              />
            </Wrap>
            <Wrap>
              <AppBarDropDown
                label={t('haynespro.select_reseller_dropdown')}
                value={reseller || ''}
                onChange={eventData => {
                  setReseller(eventData.target.value)
                }}
                sx={style.optionSelector}
                disabled={disabledToolbar}
                options={distributorParamsResult.data?.ResellerOptions || []}
              />
            </Wrap>
            <Wrap>
              <AppBarDropDown
                label={t('haynespro.select_product_type_short_dropdown')}
                value={productType || ''}
                onChange={eventData => {
                  setProductType(eventData.target.value)
                }}
                sx={style.optionSelector}
                disabled={disabledToolbar}
                options={distributorParamsResult.data?.productTypeOptions || []}
              />
            </Wrap>
            <Wrap>
              <AppBarDatePicker
                value={startDate}
                label={t('haynespro.filter_start_date')}
                disabled={disabledToolbar}
                onChange={e => handleChange(e, setStartDate)}
              />
            </Wrap>
            <Wrap>
              <AppBarDatePicker
                value={endDate}
                label={t('haynespro.filter_end_date')}
                disabled={disabledToolbar}
                onChange={e => handleChange(e, setEndDate)}
              />
            </Wrap>
          </Box>

          <IconButton
            onClick={() => searchLicenses()}
            color='secondary'
            disabled={disabledToolbar}
            data-testid='btn-search-licenses'
            size='large'
          >
            <IconSearch />
          </IconButton>
          <HaynesProMoreActionsMenu
            disableRenewLicenses={!Array.from(rowSelections).some(([_, selected]) => selected) || isRenewingLicenses}
            handleRenewLicenses={() => renewSelectedLicenses()}
            distributorUUID={distributor?.Distributor?.UUID || ''}
            disabled={disabledToolbar}
            onLinkerStart={handleMessage}
            onLinkerError={handleMessage}
            onLinkerFinish={handleMessage}
          />
        </Toolbar>
      </AppBar>
      {isRenewingLicenses && <LinearProgress variant='determinate' value={(renewingProgress / rowSelections.size) * 100} />}
      <div>
        <HaynesProMessage message={message} />
        <HaynesProLicensesTable
          rows={rows || []}
          startDate={startDate}
          endDate={endDate}
          rowSelections={rowSelections}
          setRowSelections={setRowSelections}
        />
      </div>
    </>
  )
}

export default HaynesProLicensesOverview
